import React, { useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { PublishingRuleCreateSchema } from "../../types";

import ExcludedDaysSection from "./ExcludedDaysSection";
import PeriodSection from "./PeriodSection";
import { PublicationSection } from "./PublicationSection";
import PublishingDaysSection from "./PublishingDaysSection";
import StoptimesSection from "./StoptimesSection";
import { validateSchedule, ValidationErrors } from "./validation";
export interface EditScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (schedule: PublishingRuleCreateSchema) => void;
  initialSchedule?: PublishingRuleCreateSchema;
}

export const EditScheduleDialog: React.FC<EditScheduleDialogProps> = ({
  initialSchedule,
  open,
  onClose,
  onSubmit,
}) => {
  const [schedule, setSchedule] = useState<PublishingRuleCreateSchema>({
    from_date: initialSchedule?.from_date ?? "",
    to_date: initialSchedule?.to_date ?? "",
    monday: initialSchedule?.monday ?? false,
    tuesday: initialSchedule?.tuesday ?? false,
    wednesday: initialSchedule?.wednesday ?? false,
    thursday: initialSchedule?.thursday ?? false,
    friday: initialSchedule?.friday ?? false,
    saturday: initialSchedule?.saturday ?? false,
    sunday: initialSchedule?.sunday ?? false,
    days_exceptions: initialSchedule?.days_exceptions ?? [],
    hour: initialSchedule?.hour ?? 0,
    minute: initialSchedule?.minute ?? 0,
    publication_lead_time: initialSchedule?.publication_lead_time ?? 1,
    days_type: initialSchedule?.days_type ?? "business_days",
    times_exceptions: initialSchedule?.times_exceptions ?? [],
    publications: initialSchedule?.publications ?? [],
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const { t } = Bananas.useI18n();

  const handleSubmit = () => {
    const newErrors = validateSchedule(schedule);
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit(schedule);
      onClose();
      setSchedule({
        from_date: initialSchedule?.from_date ?? "",
        to_date: initialSchedule?.to_date ?? "",
        monday: initialSchedule?.monday ?? false,
        tuesday: initialSchedule?.tuesday ?? false,
        wednesday: initialSchedule?.wednesday ?? false,
        thursday: initialSchedule?.thursday ?? false,
        friday: initialSchedule?.friday ?? false,
        saturday: initialSchedule?.saturday ?? false,
        sunday: initialSchedule?.sunday ?? false,
        days_exceptions: initialSchedule?.days_exceptions ?? [],
        hour: initialSchedule?.hour ?? 0,
        minute: initialSchedule?.minute ?? 0,
        publication_lead_time: initialSchedule?.publication_lead_time ?? 1,
        days_type: initialSchedule?.days_type ?? "business_days",
        times_exceptions: initialSchedule?.times_exceptions ?? [],
        publications: initialSchedule?.publications ?? [],
      });
      setErrors({});
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 8,
        },
      }}
      onClose={onClose}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", pt: 4 }}>
        <Typography fontSize={24}>
          {initialSchedule
            ? t("Ändra utgivningsschema")
            : t("Skapa utgivningsschema")}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {initialSchedule
            ? t(
                "Ändra ett befintligt utgivningsschema för en eller flera titlar.",
              )
            : t("Skapa ett nytt utgivningsschema för en eller flera titlar.")}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack spacing={3} sx={{ mt: 2, pb: 3 }}>
          <PeriodSection
            error={errors.from_date || errors.to_date}
            schedule={schedule}
            setSchedule={(newSchedule) => {
              setSchedule(newSchedule);
              setErrors({});
            }}
          />

          <PublishingDaysSection
            error={errors.publicationDays}
            schedule={schedule}
            setSchedule={(newSchedule) => {
              setSchedule(newSchedule);
              setErrors({});
            }}
          />

          <ExcludedDaysSection
            schedule={schedule}
            setSchedule={(newSchedule) => {
              setSchedule(newSchedule);
              setErrors({});
            }}
          />

          <StoptimesSection
            schedule={schedule}
            setSchedule={(newSchedule) => {
              setSchedule(newSchedule);
              setErrors({});
            }}
          />

          <PublicationSection
            error={errors.publications}
            isLoadingPublications={false}
            selectedPublications={schedule.publications}
            onPublicationsChange={(publications) => {
              setSchedule((prev) => ({ ...prev, publications }));
              setErrors({});
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>{t("Avbryt")}</Button>
        <Button
          disabled={Object.keys(errors).length > 0}
          variant="text"
          onClick={handleSubmit}
        >
          {initialSchedule ? t("Spara ändringar") : t("Skapa utgivningsschema")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduleDialog;
